// @ts-strict-ignore
import { DefaultProjectHelper } from '@project/helper/default-project.helper';
import { Connection } from '@3d-scene/model3d/connection';
import { TextureAreaBorderParameters } from '@3d-scene/model/texture-area-border-parameters';
import { LabelingUtils } from '@3d-scene/model3d/util/labeling-utils';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { SelectionArticleGroupIdentity } from '@project/components/article/model/vo/selection-article-group-identity';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { CompositeArticleUtils } from '@article/util/composite-article-utils';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { ArticleBehaviorPropertyDef } from '@article/constants/article-behavior-property-def';
import { InsertArticleHelper } from '@article/helper/insert-article.helper';
import { AdvancedLine } from '@3d-scene/model/advanced-line';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ConnectionAreaVO, MetaElementUsageIdentityVO } from '@shared/vo/meta-element.vo';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { Dictionary } from 'typescript-collections';
import { Vector3 } from 'three';
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { PropertyUtils } from '@article/util/property-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { UndoDetachArticlesOperation } from '@shared/undo/operation/undo-detach-articles-operation';
import { UserVO } from '@user/model/vo/user.vo';
import { AlertButton } from '@shared/alert/alertButton';
import { ProjectVO } from '@project/model/vo/project.vo';
import { UserModel } from '@user/model/user.model';
import { AlertHelper } from '@shared/alert/alert.helper';
import { UndoRedoHelper } from '@shared/undo/undo-redo.helper';
import { ProjectModel } from '../model/project.model';
import { InsertArticleHelperFactory } from '@article/helper/insert-article-helper.factory';
import { Injectable } from '@angular/core';
import { ProjectModelService } from '../service/project-model.service';
import { ProjectArticleService } from '../service/project-article.service';
import { ProjectMetaElementService } from '../manager/project-meta-element.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { UserPermissionName } from '@user/constants/user-permissions';
import { CompositeArticleInfoUtils } from '@article/util/composite-article-info-utils';
import { SequenceOperation } from '@shared/undo/operation/sequence-operation';

@Injectable({ providedIn: 'root' })
export class McsGdsBaseProjectHelper extends DefaultProjectHelper {
  private readonly PART_OF_DEFAULT_LABELING_TEXTURE_BORDER_COLOR: number = 0x0000ff;
  private readonly VISIBLE_MCS_GDS_SERIES: string[] = ['769']; // SMAR-190: Further series currently excluded: , "2020", "2022"];

  constructor(
    userModel: UserModel,
    projectModel: ProjectModel,
    projectModelService: ProjectModelService,
    projectArticleService: ProjectArticleService,
    projectMetaElementService: ProjectMetaElementService,
    undoRedoHelper: UndoRedoHelper,
    alertHelper: AlertHelper,
    compositeArticleUtils: CompositeArticleUtils,
    containerService: ContainerService,
    labelingUtils: LabelingUtils,
    articleContainerUtils: ArticleContainerUtils,
    private articleConnectionService: ArticleConnectionService,
    private insertArticleHelperFactory: InsertArticleHelperFactory,
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
  ) {
    super(
      userModel,
      projectModel,
      projectModelService,
      projectArticleService,
      projectMetaElementService,
      undoRedoHelper,
      alertHelper,
      compositeArticleUtils,
      containerService,
      labelingUtils,
      articleContainerUtils
    );
  }

  public createSelection(articleUsage: ArticleUsageVO): SelectionArticleGroupIdentity {
    // this selects only 1 usage instead of all usages belonging to a complex article
    const selectionGroupIdentity: SelectionArticleGroupIdentity = new SelectionArticleGroupIdentity(articleUsage.groupId, [articleUsage.id]);
    return selectionGroupIdentity;
  }

  public createTextureAreaBorder(connection: Connection): TextureAreaBorderParameters {
    if (!connection) {
      return null;
    }
    let textureBorderParameters: TextureAreaBorderParameters = null;
    if (this.labelingUtils.isTextureCubePartOfDefaultLabeling(connection)) {
      textureBorderParameters = this.createPartOfDefaultLabelingBorderParameters();
    } else {
      textureBorderParameters = this.createTextureAreaBorderByDefault(connection);
    }
    return textureBorderParameters;
  }

  public isMoveArticleAllowed(articleContainer: ArticleContainer): boolean {
    if (!articleContainer) {
      return false;
    }
    if (this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer)) {
      return !this.compositeArticleUtils.isCompositeArticleConnectedToRail(articleContainer);
    } else {
      return !this.articleConnectionService.isArticleConnectedToMountingRail(articleContainer);
    }
  }

  protected isStrippingComplexArticleRequired(): boolean {
    return !this.userHasPermissionToDeleteMainPartComplexArticle();
  }

  public isDeleteCompositePartArticleAllowed(articleContainer: ArticleContainer): boolean {
    return this.compositeArticleUtils.isCompositeArticleConnectedToRail(articleContainer)
      ? this.userHasPermissionToDeleteMainPartComplexArticle()
      : false;
  }

  protected createPartOfDefaultLabelingBorderParameters(): TextureAreaBorderParameters {
    const textureBorderParameters: TextureAreaBorderParameters = new TextureAreaBorderParameters();
    textureBorderParameters.borderColor = this.PART_OF_DEFAULT_LABELING_TEXTURE_BORDER_COLOR;
    textureBorderParameters.drawOuterBorder = true;
    textureBorderParameters.forceDrawBorder = true;
    return textureBorderParameters;
  }

  public isInitialRotationRequired(articleContainer: ArticleContainer): boolean {
    if (!articleContainer) {
      return false;
    }
    let article: ArticleVO = null;
    if (this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer, true)) {
      article = this.compositeArticleUtils.getCompositeArticle(articleContainer, true);
    } else if (this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer, false)) {
      article = this.compositeArticleUtils.getCompositeArticle(articleContainer, false);
    } else {
      article = articleContainer.article;
    }
    return article == null
      ? false
      : PropertyUtils.getBooleanPropertyValue(article.behaviorProperties, ArticleBehaviorPropertyDef.IS_INITIAL_ROTATION_IN_MCS_GDS_REQUIRED);
  }

  public considerOriginalAppliedRotationForReplacement(): boolean {
    return false;
  }

  protected async proceedDelete(articleUsageIds: string[], deleteFunction: (articleUsageIdsToRemove: string[]) => Promise<SequenceOperation>): Promise<void> {
    const filteredArticleUsageIds: string[] = [];
    let insertArticleHelper: InsertArticleHelper = null;
    let moveLine: AdvancedLine = null;
    let canMoveAfterDeletion: boolean = true;
    const articleUsageIdsToDelete: string[] = this.containerService.collectForwardConnectedArticleUsageIds(articleUsageIds);
    for (const articleUsageId of articleUsageIds) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
      const parentConnectionUsageIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
        articleContainer.articleUsage
      );
      if (parentConnectionUsageIds.length > 0) {
        const parentConnectionId: MetaElementUsageIdentityVO = parentConnectionUsageIds[0];
        const parentArticleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(parentConnectionId.articleUsageId);
        const acceptingConnectionArea: ConnectionAreaVO = parentArticleContainer.findMetaElementByName(
          parentConnectionId.metaElementName
        ) as ConnectionAreaVO;
        if (ConnectionInfoUtils.isRangeOnLineConnection(acceptingConnectionArea)) {
          if (!insertArticleHelper) {
            insertArticleHelper = this.insertArticleHelperFactory.createInstanceByAcceptingContainer(
              parentArticleContainer,
              acceptingConnectionArea.name
            );
          }

          let moveMap: Dictionary<string, Vector3> = null;
          const replaceArticleLine: AdvancedLine = insertArticleHelper.calculateCommonArticlesLine([articleContainer]);
          if (replaceArticleLine) {
            const articleLine: AdvancedLine = new AdvancedLine(replaceArticleLine.start, replaceArticleLine.start);
            moveMap = insertArticleHelper.replaceArticle(replaceArticleLine, articleLine, articleUsageIdsToDelete, false);
          }

          if (!moveMap) {
            canMoveAfterDeletion = false;
            continue;
          }
          const replacedArticleLine: AdvancedLine = insertArticleHelper.getArticleLine(articleContainer);
          if (!moveLine || moveLine.start.length() > replacedArticleLine.start.length()) {
            // This Zero-length line marks the left end of the replaced article.
            // For comparisons with other ArticleLines ee also must copy the left overlap.
            moveLine = new AdvancedLine(replacedArticleLine.start, replacedArticleLine.start, replacedArticleLine.overlapInfo.copyOverlapLeftOnly());
          }
        }
      }
      filteredArticleUsageIds.push(articleUsageId);
    }
    const sequenceOperation: SequenceOperation = await deleteFunction(filteredArticleUsageIds);
    const undoOperation: UndoDetachArticlesOperation = sequenceOperation.operations.find((operation) => operation instanceof UndoDetachArticlesOperation);
    let moveMap: Dictionary<string, Vector3> = null;
    if (insertArticleHelper && moveLine) {
      moveMap = insertArticleHelper.calculateMoveOffsetByArticleLine(moveLine, null, filteredArticleUsageIds, true, true, true, false, false, false, true);
    }
    if (undoOperation) {
      undoOperation.setMoveArticlesMap(moveMap);
      await this.undoRedoHelper.pushUndoOperation(sequenceOperation);
    }

    if (!canMoveAfterDeletion) {
      this.alertHelper.showAlert('global.label.delete', 'main.message.delete_not_deletable', {}, [AlertButton.OK], "info");
    }
  }

  protected existsNotDeletableArticles(articleUsageIds: string[]): boolean {
    const existsNotDeletableArticles: boolean = super.existsNotDeletableArticles(articleUsageIds);
    if (existsNotDeletableArticles) {
      return existsNotDeletableArticles;
    } else {
      return this.existsNotDeletableAcessoryCompositionArticles(articleUsageIds);
    }
  }

  private existsNotDeletableAcessoryCompositionArticles(articleUsageIds: string[]): boolean {
    if (this.userModel.user.hasPermission("DELETE_ALL_MCS_ARTICLE_COMPONENTS")) {
      return false;
    }
    articleUsageIds = this.containerService.collectForwardConnectedArticleUsageIds(articleUsageIds);
    const notDeletableAccessoryParts: ArticleContainer[] = this.collectNotDeletableAccessoryParts(articleUsageIds);
    for (const articleContainer of notDeletableAccessoryParts) {
      const parentAcceptingAreaIds: MetaElementUsageIdentityVO[] = ArticleUsageUtils.getBackwardConnectedConnectionUsageIds(
        articleContainer.articleUsage
      );
      for (const parentAcceptingAreaId of parentAcceptingAreaIds) {
        const parentUsage: ArticleUsageVO = this.projectModel.getArticleUsage(parentAcceptingAreaId.articleUsageId);
        const strippedCompositeId: number = PropertyUtils.getNumberPropertyValue(
          parentUsage.infoProperties,
          ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_ID
        );
        if (ArrayUtils.getItemIndex(parentAcceptingAreaId.articleUsageId, articleUsageIds) === -1 && !isNaN(strippedCompositeId)) {
          return true;
        }
      }
    }
    return false;
  }

  private collectNotDeletableAccessoryParts(articleUsageIds: string[]): ArticleContainer[] {
    const result: ArticleContainer[] = [];
    for (const articleUsageId of articleUsageIds) {
      const articleContainer: ArticleContainer = this.containerService.findArticleContainerInGlobal(articleUsageId);
      if (!articleContainer) {
        continue;
      }
      const strippedCompositeId: number = PropertyUtils.getNumberPropertyValue(
        articleContainer.articleUsage.infoProperties,
        ArticleUsageInfoPropertyDef.STRIPPED_COMPOSITE_ARTICLE_ID
      );
      if (!isNaN(strippedCompositeId)) {
        result.push(articleContainer);
      }
    }
    return result;
  }

  protected userHasPermissionToDeleteMainPartComplexArticle(): boolean {
    return false;
  }

  protected checkUserPermission(user: UserVO, permission: UserPermissionName): boolean {
    if (!user) {
      user = this.userModel.user;
    }
    return user && user.hasPermission(permission);
  }

  public isRotateArticleAllowed(articleContainer: ArticleContainer): boolean {
    if (this.compositeArticleInfoUtils.isCompositePartArticle(articleContainer)) {
      return !this.compositeArticleUtils.isCompositeArticleConnectedToRail(articleContainer);
    } else {
      return !this.articleConnectionService.isArticleConnectedToMountingRail(articleContainer);
    }
  }

  /** SMAR-190: For certain MCS GDS base articles (if present in project), structure can be viewed by everyone */
  protected isViewProjectStructureAllowedBecauseOfMCSGDSBaseArticle(project: ProjectVO): boolean {
    if (!project) {
      return false;
    }
    const baseArticleNo: string = this.projectModel.currentInfoProperties.getString("mcsGdsInitArticleNo");
    if (!baseArticleNo) {
      return false;
    }
    const series: string = baseArticleNo.split('-')[0];
    return this.VISIBLE_MCS_GDS_SERIES.indexOf(series) !== -1;
  }

  public isLabelingCorrectionDisabled(): boolean {
    return true;
  }
}
